// pages/Editor/tree/treeUtils.ts
import type { UniqueIdentifier } from '@dnd-kit/core';
import { AutomationStep, AutomationStepWithDirectChildren } from '@/types/automationSteps';
import { ConditionAction, StepType } from '@/types/automationFields';
import { arrayMove } from '@dnd-kit/sortable';
import { getActiveConditionId, isStepWithChildren } from '@/utils/stepUtils.ts';

export type FlattenedStep = {
  id: UniqueIdentifier;
  step: AutomationStep;
  parentId: UniqueIdentifier | null; // null for root
  depth: number;
};

export type Projection = {
  depth: number;
  parentId: UniqueIdentifier | null;
  index: number; // zero-based
};

function getDragDepth(offset: number, indentationWidth: number) {
  return Math.round(offset / Math.max(1, indentationWidth));
}

export function canHaveChildren(step: AutomationStep, activeConditionIds: Record<string, string>) {
  if (step.step_type === StepType.Conditional) {
    const activeConditionId = getActiveConditionId(step, activeConditionIds);
    const condition = step.conditions.find((c) => c.condition_id === activeConditionId);
    return condition ? condition.action === ConditionAction.ChildSteps : false;
  } else {
    return isStepWithChildren(step);
  }
}

/** Remove all descendants of any id in `excludeIds` from a flattened list */
export function removeDescendants(
  items: FlattenedStep[],
  excludeIds: UniqueIdentifier[],
): FlattenedStep[] {
  const exclude = new Set(excludeIds.map(String));
  const out: FlattenedStep[] = [];
  for (const row of items) {
    const pid = row.parentId != null ? String(row.parentId) : null;
    if (pid && exclude.has(pid)) {
      // cascade the exclusion to all deeper descendants
      exclude.add(String(row.id));
      continue;
    }
    out.push(row);
  }
  return out;
}

/** Count siblings with same parentId that occur before cutoffIdx in a given flat list,
 *  pretending the dragged (activeId) row is already removed from earlier positions.
 */
function siblingsBeforeIn(
  items: FlattenedStep[],
  parentId: UniqueIdentifier | null,
  cutoffIdx: number,
  activeId: UniqueIdentifier,
) {
  let idx = 0;
  for (let i = 0; i < cutoffIdx && i < items.length; i++) {
    const row = items[i];
    if (row.id === activeId) continue;
    if (row.parentId === parentId) idx++;
  }
  return idx;
}

export function getProjection(
  items: FlattenedStep[],
  activeId: UniqueIdentifier,
  overId: UniqueIdentifier,
  dragOffset: number,
  indentationWidth: number,
  activeConditionIds: Record<string, string>,
): Projection {
  const overItemIndex = items.findIndex(({ id }) => id === overId);
  const activeItemIndex = items.findIndex(({ id }) => id === activeId);
  const activeItem = items[activeItemIndex];

  // Safety: if we can't resolve, drop at root index 0
  if (overItemIndex === -1 || activeItemIndex === -1 || !activeItem) {
    return { depth: 0, parentId: null, index: 0 };
  }

  // 1) Pretend the item is moved to the over position (treat `over` as the **next** row)
  const newItems = arrayMove(items, activeItemIndex, overItemIndex);

  // 2) Desired depth from horizontal drag
  const dragDepth = getDragDepth(dragOffset, indentationWidth);
  const projectedDepth = activeItem.depth + dragDepth;

  // 3) Clamp depth between prev/next (prev can be a parent iff canHaveChildren)
  const previousItem = newItems[overItemIndex - 1];
  const nextItem = newItems[overItemIndex + 1];

  const maxDepth = previousItem
    ? previousItem.depth + (canHaveChildren(previousItem.step, activeConditionIds) ? 1 : 0)
    : 0;

  const minDepth = nextItem ? nextItem.depth : 0;

  let depth = projectedDepth;
  if (depth > maxDepth) depth = maxDepth;
  if (depth < minDepth) depth = minDepth;

  // 4) Resolve parentId at that depth (same as the official example)
  let parentId = getParentId();
  function getParentId(): UniqueIdentifier | null {
    if (depth === 0 || !previousItem) return null;
    if (depth === previousItem.depth) return previousItem.parentId;
    if (depth > previousItem.depth) return previousItem.id;

    const found = newItems
      .slice(0, overItemIndex)
      .reverse()
      .find((it) => it.depth === depth);
    return found ? (found.parentId ?? null) : null;
  }

  // Optional belt-and-suspenders: never allow parenting to itself
  if (String(parentId ?? '') === String(activeId)) {
    parentId = previousItem ? (previousItem.parentId ?? null) : null;
    depth = previousItem ? previousItem.depth : 0;
  }

  // 5) zero-based index among siblings under parentId at the insertion point
  const index = siblingsBeforeIn(newItems, parentId, overItemIndex, activeId);

  return { depth, parentId, index };
}

// --- flatten ---

export function flattenTree(
  steps: AutomationStep[],
  activeConditionIds: Record<string, string>,
  parentId: UniqueIdentifier | null = null,
  depth = 0,
): FlattenedStep[] {
  const out: FlattenedStep[] = [];
  for (const s of steps) {
    out.push({ id: s.step_id, step: s, parentId, depth });
    if (s.step_type === StepType.Conditional) {
      const activeConditionId = getActiveConditionId(s, activeConditionIds);
      const condition = s.conditions.find((c) => c.condition_id === activeConditionId);
      const children =
        condition && condition.action === ConditionAction.ChildSteps ? condition.children : [];
      out.push(...flattenTree(children, activeConditionIds, s.step_id, depth + 1));
    } else if (s.step_type === StepType.FailureHandler) {
      const activeGroup = getActiveConditionId(s, activeConditionIds);
      const children = activeGroup === 'handler' ? s.handler_children : s.children;
      out.push(...flattenTree(children, activeConditionIds, s.step_id, depth + 1));
    } else if ([StepType.Iterate, StepType.Repeat, StepType.Retry].includes(s.step_type)) {
      const children = (s as AutomationStepWithDirectChildren).children as AutomationStep[];
      out.push(...flattenTree(children, activeConditionIds, s.step_id, depth + 1));
    }
  }
  return out;
}
